import React, { useEffect, useRef } from 'react';
import { Button } from '../../components/button/button';
import { ButtonSize } from '../../core/enums';
import theme from '../../theme';
import { Flex, Text } from '@chakra-ui/react';
import { styles } from './sso-login-styles';
import LoadingOverlay from '../../components/loader-overlay/loader-overlay';
import {
  describeSsoCallbackLocation,
  logSsoLogin
} from '../../core/sso-login-telemetry';

const classes = styles();

type SSOLoginProps = {
  loading: boolean;
  match: any;
  loginViaSSO: (loginData: any) => void;
  exchangeTokenSso: (token: string) => void;
};

const SSOLogin: React.FC<SSOLoginProps> = (props: SSOLoginProps) => {
  const { loading, match, loginViaSSO, exchangeTokenSso } = props;
  const lastExchangedToken = useRef<string | null>(null);
  const exchangeTokenRef = useRef(exchangeTokenSso);
  exchangeTokenRef.current = exchangeTokenSso;

  const hash = window.location.hash.substr(1);
  let idToken = '';
  const searchParams = new URLSearchParams(hash);

  idToken = searchParams.get('id_token') ?? '';

  const orgCode = match?.params?.orgCode?.toUpperCase();

  const handleLogin = () => {
    logSsoLogin('federated_signin_click', { orgCode });
    loginViaSSO({ orgCode });
  };

  useEffect(() => {
    const locationInfo = describeSsoCallbackLocation();
    logSsoLogin('callback_page', { orgCode, loading, ...locationInfo });
    if (locationInfo.cognitoError) {
      logSsoLogin(
        'callback_cognito_error',
        { orgCode, ...locationInfo },
        'error'
      );
    }
    // Snapshot the landing URL once per org; loading toggles are not diagnostic here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCode]);

  useEffect(() => {
    if (idToken.length === 0) {
      const locationInfo = describeSsoCallbackLocation();
      const looksLikeCallback = Boolean(
        locationInfo.hashPresent ||
          locationInfo.hasIdToken ||
          locationInfo.hasAccessToken ||
          locationInfo.hasAuthCode ||
          locationInfo.cognitoError
      );
      if (looksLikeCallback) {
        logSsoLogin(
          'skip_exchange_no_id_token',
          { orgCode, ...locationInfo },
          locationInfo.cognitoError ? 'error' : 'warn'
        );
      }
      return;
    }
    if (lastExchangedToken.current === idToken) {
      logSsoLogin('skip_exchange_already_exchanged', {
        orgCode,
        idTokenLength: idToken.length
      });
      return;
    }
    lastExchangedToken.current = idToken;
    logSsoLogin('exchange_dispatch', {
      orgCode,
      idTokenLength: idToken.length
    });
    // Drop Cognito hash so remounts cannot re-read id_token from the URL.
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
    exchangeTokenRef.current(idToken);
  }, [idToken, orgCode]);

  return (
    <>
      {loading ? (
        <div className="centered-application">
          <div className="centered-application-wrapper">
            <LoadingOverlay loadingMsg="Logging you in..." />
          </div>
        </div>
      ) : (
        <Flex className="sso-form" sx={classes.ssoForm}>
          <Text sx={classes.ssoFormText}>
            Please click the login button below to authenticate with your
            organization. Once authenticated, you will be redirected back to
            CodaMetrix and automatically logged into the application.
          </Text>
          <Flex className="form-row" sx={classes.btnHeader}>
            <Button
              className="qa-sso-button"
              size={ButtonSize.MD}
              label={'Login'}
              backgroundColor={theme.colors.accent[100]}
              color={theme.colors.text['light']}
              disabled={loading}
              name="SSO Login"
              dataTestId="sso_login"
              onClick={handleLogin}
            />
          </Flex>
        </Flex>
      )}
    </>
  );
};

SSOLogin.displayName = 'SSOLogin';
export default SSOLogin;
